import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { RoleModel } from '../../../role.model';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { RoleHelperService } from '../../../services/role-helper.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiEditObjectDialogComponent } from '@fi-shared/fi-edit-object-dialog/fi-edit-object-dialog.component';
import { ValidationErrors } from '@angular/forms';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { ExtendedRoleRightModel } from '@fi-app/role/extended-role-right.model';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';

export enum RoleGeneralInfoClickTarget {
    Card,
    Icon,
}

@Component({
    selector: 'fi-role-general-info',
    templateUrl: './role-general-info.component.html',
    styleUrls: ['./role-general-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-role-general-info' },
    providers: [{ provide: FiComponentsContainer, useExisting: RoleGeneralInfoComponent }],
    standalone: false,
})
export class RoleGeneralInfoComponent extends FiComponentsContainer {
    @Input() isEditMode: boolean;
    @Input() role: RoleModel;
    @Input() scrollableElementRefs: ElementRef[];
    @Output() onExtendedRightsClick = new EventEmitter<void>();

    RoleGeneralInfoClickTarget = RoleGeneralInfoClickTarget;
    readonly defaultRoleDescriptionLabelId: string = 'role.role_description_hint';
    readonly defaultRoleNameLabelId: string = 'role.role_name';

    constructor(
        private contentLanguagesService: ContentLanguagesService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private cdr: ChangeDetectorRef,
        private roleHelperService: RoleHelperService,
        private detectDevicesService: DetectDevicesService,
        private saveChangesWarningService: SaveChangesWarningService,
    ) {
        super();
    }

    getRoleNameAndDescription(labelsArray: IContentLanguageLabelModel[], defaultLabel?: string): string {
        const langId: number = this.contentLanguagesService.getSelectedLanguageIdByApplicationLang(labelsArray);
        const defaultContentLanguageValue: string = labelsArray.find(
            (languageLabelModel: IContentLanguageLabelModel) => {
                return languageLabelModel.ContentLanguageId === langId;
            },
        )?.Value;

        return defaultContentLanguageValue ? defaultContentLanguageValue : this.getDefaultLabel(defaultLabel);
    }

    private getDefaultLabel(defaultLabel: string): string {
        return this.isEditMode ? this.appTextService.get(defaultLabel) : '';
    }

    getTooltipText(): string {
        return this.appTextService.get('role.edit_role_presentation');
    }

    openEditNamesDialog(clickTarget: RoleGeneralInfoClickTarget) {
        if (this.isCardClickIgnored(clickTarget)) return;

        this.fiDialogsService
            .openAnimatedBottomDialog({
                contentComponentRef: FiEditObjectDialogComponent,
                contentComponentData: {
                    item: this.role,
                    isNameRequired: true,
                    nameInputLabel: this.appTextService.get('role.role_name'),
                    descriptionInputLabel: this.appTextService.get('common.description'),
                    useApplicationLanguages: true,
                },
                headerText: this.appTextService.get('role.edit_name_dialog_header'),
                yesButtonText: this.appTextService.get('done'),
                config: {
                    closeOnNavigation: false,
                },
            })
            .onYesButtonClick((result: RoleModel) => this.setNewRoleNames(result))
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));
        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    goToExtendedRights() {
        this.onExtendedRightsClick.emit();
    }

    private isCardClickIgnored(clickTarget: RoleGeneralInfoClickTarget): boolean {
        return clickTarget === RoleGeneralInfoClickTarget.Card && !this.isMobilePhone();
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    validateRoleNamesFn = (): ValidationErrors => {
        return {
            required: this.role.Names.every((roleName) => !roleName.Value?.trim().length),
        };
    };

    validateExtendedRoleRightsFn = (): ValidationErrors => {
        return this.roleHelperService.getExtendedRoleRightsValidationErrors(this.role, this.role.ExtendedRoleRight);
    };

    private setNewRoleNames(newRole: RoleModel) {
        this.role.Names = this.formatRoleContentLangModels(newRole.Names);
        this.role.Descriptions = this.formatRoleContentLangModels(newRole.Descriptions);
        this.roleHelperService.setRoleDataChanged(true);
        this.cdr.markForCheck();
    }

    private formatRoleContentLangModels(contentLangModels: IContentLanguageLabelModel[]) {
        return contentLangModels.map((model) => {
            return { ...model, Value: model.Value?.trim() };
        });
    }

    protected readonly FiTooltipContentType = FiTooltipContentType;
}
